import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface EndpointLocation {
  id: number;
  deviceId: string;
  deviceName: string | null;
  interfaceId: string | null;
  interfaceName: string | null;
  vlanId: number | null;
  firstSeenAt: string;
  lastSeenAt: string;
  isCurrent: boolean;
}

/** Set when the endpoint's MAC is itself a managed device (chassis/LLDP MAC). */
export interface ManagedDeviceRef {
  id: string;
  name: string | null;
}

/** The single resolved attachment point — *the* place we think the MAC is. */
export interface ComputedLocation {
  deviceId: string;
  deviceName: string | null;
  interfaceId: string | null;
  interfaceName: string | null;
  vlanId: number | null;
  /** `single_location` (one sighting) or `fdb_edge` (fewest-MAC port — assumed). */
  method: string | null;
  /** "Located here since" — when the resolved port last changed. */
  since: string | null;
}

/** Where the endpoint was last seen, when it isn't on any port right now.
 *  Populated only on the single-endpoint detail response. */
export interface LastKnownLocation {
  deviceId: string;
  deviceName: string | null;
  interfaceId: string | null;
  interfaceName: string | null;
  vlanId: number | null;
  /** When the endpoint was last seen here — i.e. how long ago it went quiet. */
  lastSeenAt: string;
}

/** One place an endpoint is attached: its wireless association, or a switch port it is seen on
 *  that doesn't lead to another device. Detail response only. */
export interface EndpointLink {
  /** The attachment's id for the link traffic verdict. */
  linkId: string;
  kind: 'wired' | 'wireless';
  deviceId: string;
  deviceName: string | null;
  interfaceId: string | null;
  interfaceName: string | null;
  vlanId: number | null;
  ssid: string | null;
  band: string | null;
  channel: number | null;
  signalDbm: number | null;
  lastSeenAt: string | null;
  /** Set when the endpoint is only seen through this neighbour (the port leads to it). */
  behindDeviceId: string | null;
  behindDeviceName: string | null;
}

export interface Endpoint {
  id: string;
  organizationId: string;
  macAddress: string;
  ouiVendor: string | null;
  hostname: string | null;
  ipAddresses: string[];
  classification: string | null;
  /** `auto`, `manual`, or null (never classified). `manual` means the operator
   *  overrode the auto-classification — the periodic classifier won't touch it. */
  classificationSource: string | null;
  notes: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  currentLocations: EndpointLocation[];
  computedLocation: ComputedLocation | null;
  /** Set (on the detail response) when the endpoint isn't currently located —
   *  the most recent place it was seen, so the UI can show "last seen … ago". */
  lastKnownLocation: LastKnownLocation | null;
  /** Non-null when this endpoint *is* a managed device — cross-link to it. */
  managedDevice: ManagedDeviceRef | null;
  /** Where it is attached. Detail response only; empty on the list. */
  links?: EndpointLink[];
}

export interface EndpointListResponse {
  endpoints: Endpoint[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListParams {
  search?: string;
  ouiVendor?: string;
  classification?: string;
  /** Comma-separated classifications to include (OR). */
  classifications?: string;
  /** Comma-separated classifications to exclude. */
  excludeClassifications?: string;
  /** Hide endpoints that are themselves a managed device (shown under Equipment). */
  excludeManaged?: boolean;
  seenSince?: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.search) usp.set('search', params.search);
  if (params.ouiVendor) usp.set('oui_vendor', params.ouiVendor);
  if (params.classification) usp.set('classification', params.classification);
  if (params.classifications) usp.set('classifications', params.classifications);
  if (params.excludeClassifications) usp.set('exclude_classifications', params.excludeClassifications);
  if (params.excludeManaged) usp.set('exclude_managed', 'true');
  if (params.seenSince) usp.set('seen_since', params.seenSince);
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export function useEndpoints(params: ListParams = {}) {
  const contextReady = useActiveContextReady();
  return useQuery<EndpointListResponse>({
    queryKey: ['endpoints', params],
    queryFn: async () => {
      const response = await axios.get<EndpointListResponse>(
        `${config.backendApi}/api/endpoints${buildQuery(params)}`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: contextReady,
  });
}

export function useEndpoint(id: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<Endpoint>({
    queryKey: ['endpoint', id],
    queryFn: async () => {
      if (!id) throw new Error('id is required');
      const response = await axios.get<Endpoint>(
        `${config.backendApi}/api/endpoints/${id}`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!id && contextReady,
  });
}

export function useEndpointsForDevice(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<EndpointListResponse>({
    queryKey: ['endpointsForDevice', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('deviceId is required');
      const response = await axios.get<EndpointListResponse>(
        `${config.backendApi}/api/devices/${deviceId}/endpoints`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!deviceId && contextReady,
  });
}

export function useEndpointsForInterface(interfaceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<EndpointListResponse>({
    queryKey: ['endpointsForInterface', interfaceId],
    queryFn: async () => {
      if (!interfaceId) throw new Error('interfaceId is required');
      const response = await axios.get<EndpointListResponse>(
        `${config.backendApi}/api/interfaces/${interfaceId}/endpoints`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!interfaceId && contextReady,
  });
}

export interface UpdateEndpointBody {
  hostname?: string | null;
  classification?: string | null;
  notes?: string | null;
}

export function useUpdateEndpoint(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Endpoint, Error, UpdateEndpointBody>({
    mutationFn: async (body) => {
      const response = await axios.patch<Endpoint>(
        `${config.backendApi}/api/endpoints/${id}`,
        body,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['endpoints'] });
      void queryClient.invalidateQueries({ queryKey: ['endpoint', id] });
    },
  });
}
